import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { EmblaCarouselType, EmblaEventType, EmblaOptionsType } from 'embla-carousel';
import { debounceTime, Subscription } from 'rxjs';

import { DeviceSize } from '../_shared/data/device.data';
import { DeviceService } from '../_shared/services/device.service';
import { ButtonComponent } from '../button/button.component';
import { CarouselComponent } from '../carousel/carousel.component';
import { ContainerComponent } from '../container/container.component';

@Component({
    selector: 'sirius-slider',
    templateUrl: './slider.component.html',
    styleUrl: './slider.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ContainerComponent, CarouselComponent, TranslateModule],
    host: {
        '(keydown.arrowright)': 'onArrowRight($any($event))',
        '(keydown.arrowleft)': 'onArrowLeft($any($event))',
        '(keydown.home)': 'onHome($any($event))',
        '(keydown.end)': 'onEnd($any($event))',
    },
})
export class SliderComponent implements OnInit, AfterViewInit, OnDestroy {
    private _changeDetector = inject(ChangeDetectorRef);
    private _deviceService = inject(DeviceService);

    @Input() startIndex = 0;
    @Input() loop = false;
    @Input() layout: 'constrained' | 'fluid' = 'constrained';
    @Input() slidesToScroll: 'auto' | number = 'auto';
    @Input() template?: TemplateRef<unknown>;
    /**
     * Show navigation arrows.
     * - `'auto'` (default): hidden on small devices (SM/MD), shown on LG+
     * - `true`: always shown regardless of device
     * - `false`: always hidden
     */
    @Input() showArrows: boolean | 'auto' = 'auto';
    @Input() showDots = true;

    @Output() selectedIndex = new EventEmitter<number>();
    @Output() scrollNextEvent = new EventEmitter<void>();
    @Output() scrollPreviousEvent = new EventEmitter<void>();

    @ViewChild(CarouselComponent) carouselComponent?: CarouselComponent;

    options: EmblaOptionsType = {};
    arrowsSupportedByDevice = true;

    private _deviceSubscription?: Subscription;
    private _canScroll: boolean | null = null;

    get carousel(): EmblaCarouselType | undefined {
        return this.carouselComponent?.carousel;
    }

    get arrowsVisible(): boolean {
        if (this.showArrows === 'auto') return this.arrowsSupportedByDevice;
        return this.showArrows;
    }

    ngOnInit(): void {
        this._deviceSubscription = this._deviceService.deviceSize$.pipe(debounceTime(100)).subscribe((deviceSize) => {
            this.updateCarousel(deviceSize);
        });

        this.options = {
            containScroll: 'trimSnaps',
            align: 'start',
            skipSnaps: true,
            loop: this.loop,
            inViewThreshold: 1,
            slidesToScroll: this.slidesToScroll,
            startIndex: this.startIndex,
        };
    }

    ngAfterViewInit(): void {
        // Embla measures outside Angular, so the arrow/dot bindings go stale without this.
        (['init', 'reInit', 'resize', 'slidesInView'] as EmblaEventType[]).forEach((event) =>
            this.carousel?.on(event, () => {
                this._changeDetector.detectChanges();
            })
        );

        this.carousel?.on('select', () => {
            if (!this.carousel) return;

            const selected = this.carousel.selectedScrollSnap();

            this.selectedIndex.emit(selected);
            this._changeDetector.detectChanges();
        });

        this.updateCarousel(this._deviceService.currentDeviceSize);
    }

    ngOnDestroy(): void {
        this._deviceSubscription?.unsubscribe();
    }

    hasSlidesNotInView(): boolean {
        return this.carousel ? this.carousel.slidesNotInView().length > 0 : false;
    }

    canScroll(): boolean {
        return this.carousel?.canScrollNext() || this.carousel?.canScrollPrev() || false;
    }

    scrollPrevious(): void {
        this.carousel?.scrollPrev();
        this.scrollPreviousEvent.emit();
    }

    scrollNext(): void {
        this.carousel?.scrollNext();
        this.scrollNextEvent.emit();
    }

    onArrowRight(event: KeyboardEvent): void {
        if (!this.shouldHandleKey(event) || !this.carousel?.canScrollNext()) return;
        event.preventDefault();
        this.scrollNext();
    }

    onArrowLeft(event: KeyboardEvent): void {
        if (!this.shouldHandleKey(event) || !this.carousel?.canScrollPrev()) return;
        event.preventDefault();
        this.scrollPrevious();
    }

    onHome(event: KeyboardEvent): void {
        if (!this.shouldHandleKey(event) || !this.carousel) return;
        event.preventDefault();
        this.carousel.scrollTo(0);
    }

    onEnd(event: KeyboardEvent): void {
        if (!this.shouldHandleKey(event) || !this.carousel) return;
        event.preventDefault();
        const last = this.carousel.scrollSnapList().length - 1;
        this.carousel.scrollTo(last);
    }

    private shouldHandleKey(event: KeyboardEvent): boolean {
        const target = event.target as HTMLElement | null;
        if (!target) return true;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return false;
        return !target.isContentEditable;
    }

    private updateCarousel(deviceSize: DeviceSize): void {
        const isSmallDevice = [DeviceSize.SM, DeviceSize.MD].includes(deviceSize);
        this.arrowsSupportedByDevice = !isSmallDevice;

        const scrollable = this.canScroll();
        if (!this.carousel) return;

        if (isSmallDevice && this._canScroll !== true) {
            this.carousel.reInit({ active: true });
            this._canScroll = true;
        } else if (!isSmallDevice && this._canScroll !== scrollable) {
            this.carousel.reInit({ active: scrollable });
            this._canScroll = scrollable;
        }

        this._changeDetector.markForCheck();
    }
}
